import { useEffect, useState } from 'react';
import { BILLING_EMAILS_MAX } from '@arcnet/contracts';
import {
  useBillingNotifications, useSaveBillingNotifications,
} from '../../lib/api/hooks-payments.js';
import { useSession } from '../../lib/auth/session.js';
import { ErrorBanner } from '../../components/ui/Primitives.js';
import { formatMoney } from '../../lib/format.js';

/**
 * Who gets a client's billing email, and when its balance warning goes out
 * (docs/28 B1). The client's own setting, so it is changed with tenant:write
 * rather than the billing terms' billing:write.
 */
export function BillingEmails({ tenantId, currency }: { tenantId: string; currency: string }) {
  const can = useSession((s) => s.can);
  const settings = useBillingNotifications(tenantId);
  const save = useSaveBillingNotifications(tenantId);
  const [editing, setEditing] = useState(false);
  const [threshold, setThreshold] = useState('');
  const [emails, setEmails] = useState('');

  const n = settings.data;
  // The form opens on what is saved.
  useEffect(() => {
    if (!n || editing) return;
    setThreshold(n.low_balance_threshold ?? '');
    setEmails(n.emails.join('\n'));
  }, [n, editing]);

  const submit = () => void save.mutateAsync({
    low_balance_threshold: threshold.trim() === '' ? null : threshold.trim(),
    emails: emails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean),
  }).then(() => setEditing(false), () => undefined);

  const cancel = () => { setEditing(false); save.reset(); };

  return (
    <div className="arc-card" style={{ marginTop: 16 }}>
      <div className="arc-card-head">
        <h2 className="arc-h2">Billing emails</h2>
        {n && !editing && can('tenant:write') && (
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => setEditing(true)}>
            Change
          </button>
        )}
      </div>
      <div className="arc-card-body">
        {settings.error ? <ErrorBanner error={settings.error} />
          : !n ? <div className="arc-skeleton" style={{ height: 48 }} />
          : editing ? (
            <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
              <div className="arc-grid arc-grid-2">
                <div className="arc-field" style={{ margin: 0 }}>
                  <label className="arc-label" htmlFor="low-balance-level">
                    Warn when the balance falls below
                  </label>
                  <input id="low-balance-level" className="arc-input" inputMode="decimal"
                    placeholder="Off" value={threshold} onChange={(e) => setThreshold(e.target.value)} />
                  <div className="arc-hint">In rand. Empty turns the warning off.</div>
                </div>
                <div className="arc-field" style={{ margin: 0 }}>
                  <label className="arc-label" htmlFor="billing-addresses">Send billing email to</label>
                  <textarea id="billing-addresses" className="arc-input" rows={3} value={emails}
                    placeholder="accounts@example.com" onChange={(e) => setEmails(e.target.value)} />
                  <div className="arc-hint">One address per line, up to {BILLING_EMAILS_MAX}.</div>
                </div>
              </div>
              <div className="arc-row" style={{ gap: 8, marginTop: 14 }}>
                <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending}>
                  {save.isPending ? 'Saving…' : 'Save'}
                </button>
                <button type="button" className="arc-btn arc-btn-ghost" onClick={cancel}>Cancel</button>
              </div>
            </form>
          ) : (
            <dl className="arc-kv">
              <dt>Low balance warning</dt>
              <dd>
                {n.low_balance_threshold
                  ? `Below ${formatMoney(n.low_balance_threshold, currency)}`
                  : 'Off'}
              </dd>
              <dt>Warnings go to</dt>
              <dd>
                {n.emails.length > 0
                  ? n.emails.join(', ')
                  : n.fallback.length > 0
                    ? (
                      <>
                        {n.fallback.join(', ')}{' '}
                        <span className="arc-subtle">(the account&apos;s admins and finance users)</span>
                      </>
                    )
                    : <span className="arc-subtle">Nobody yet</span>}
              </dd>
              <dt>Receipts go to</dt>
              <dd>Whoever paid{n.emails.length > 0 ? `, and ${n.emails.join(', ')}` : ''}</dd>
            </dl>
          )}
      </div>
    </div>
  );
}
